/**
 * 有休の付与ルールの、画面での並べ方。
 *
 * ## なぜ1か所に置くのか
 *
 * 参照の表と編集の画面で、**同じ並びを2回書かない。**
 * 2回書くと、片方だけ直した日に「見ていた表と保存する表が違う」になる。
 * どちらが正しいかは、次に有休が付与される月まで誰にも分からない。
 *
 * ## 数字はここに持たない
 *
 * <b>日数は1つも書かない。</b>会社ごとに違うし、法改正でも変わる。
 * サーバが返す値（会社の設定）と、サーバが返す法定の下限を並べて出す。
 * 直書きしていたころは、会社が値を変えても画面は法定表のままだった——
 * **画面が嘘をついていた。**
 */

/** 勤続年数の見出し。並びはサーバが返す granted_days と同じ順。 */
export const SERVICE_LABELS = Object.freeze([
  '0.5', '1.5', '2.5', '3.5', '4.5', '5.5', '6.5以上'
])

/**
 * 週の所定労働日数ごとの説明。
 *
 * 「1年間の所定労働日数」は**法令（労基則24条の3）の当てはめの目安**で、
 * DBには持っていない（比例付与かどうかは週の日数と1日の所定から決まる）。
 * 会社が変えられる値ではないので、ここに文言として置く。
 */
export const WORKING_DAY_LABELS = Object.freeze({
  5: { title: '週5日以上', note: '週30時間以上の方を含みます（通常の労働者）' },
  4: { title: '週4日', note: '1年間の所定労働日数 169〜216日' },
  3: { title: '週3日', note: '1年間の所定労働日数 121〜168日' },
  2: { title: '週2日', note: '1年間の所定労働日数 73〜120日' },
  1: { title: '週1日', note: '1年間の所定労働日数 48〜72日' }
})

/** サーバが返した1行を、画面で扱いやすい形にする。 */
export function toDisplayRows (rows, statutoryMinimum) {
  const minimumOf = workingDays => {
    const found = (statutoryMinimum || [])
      .find(one => one && Number(one.working_days) === Number(workingDays))
    return found ? found.granted_days : []
  }

  return (rows || []).map(row => ({
    workingDays: Number(row.working_days),
    label: WORKING_DAY_LABELS[row.working_days] || { title: '週' + row.working_days + '日', note: '' },
    grantedDays: (row.granted_days || []).slice(),
    minimumDays: minimumOf(row.working_days)
  }))
}

/**
 * その値が法定を下回っているか。
 *
 * <b>空は「下回っている」と言わない。</b>空はまだ入れていないだけで、
 * 入れ忘れとして別に断る。ここで一緒にすると、直す人が
 * 「法定を下回っている」と読んで値を大きくしようとする。
 */
export function isBelowStatutory (value, minimum) {
  if (value === '' || value === null || value === undefined) return false
  if (minimum === null || minimum === undefined) return false
  const number = Number(value)
  if (!Number.isFinite(number)) return false
  return number < Number(minimum)
}

/** 画面の値を、保存の形（サーバが受け取る形）へ戻す。 */
export function toPayload (displayRows) {
  return {
    rows: (displayRows || []).map(row => ({
      working_days: row.workingDays,
      granted_days: row.grantedDays.map(one =>
        one === '' || one === null || one === undefined ? null : Number(one))
    }))
  }
}
